import React from 'react';
import { formatDate, renderEarningsDays } from './utils';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import './EconomicEventRow.css';

// Inline styles are deliberate: the prebuilt tailwind-output.css is missing many
// utilities. Hoisted to module scope so no style object is allocated per render.
const PILL_BASE_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  padding: '2px 10px',
  borderRadius: '9999px',
  fontSize: '0.75rem',
  lineHeight: '1.25rem',
  whiteSpace: 'nowrap',
};

const PILL_STYLES = {
  positive: {
    pill: { ...PILL_BASE_STYLE, backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0' },
    value: { color: '#059669', fontWeight: 600 },
  },
  negative: {
    pill: { ...PILL_BASE_STYLE, backgroundColor: '#fef2f2', border: '1px solid #fecaca' },
    value: { color: '#dc2626', fontWeight: 600 },
  },
  neutral: {
    pill: { ...PILL_BASE_STYLE, backgroundColor: '#ffffff', border: '1px solid #e5e7eb' },
    value: { color: '#374151', fontWeight: 600 },
  },
};

const PILL_LABEL_STYLE = { color: '#6b7280', fontWeight: 500 };
const PILL_ROW_STYLE = { marginLeft: '16px', gap: '8px' };

const POSITIVE_INDICATORS = [
  'GDP', 'Employment', 'Consumer Confidence', 'Retail Sales',
  'Industrial Production', 'Manufacturing PMI', 'Services PMI'
];
const NEGATIVE_INDICATORS = [
  'Unemployment Rate', 'Initial Jobless Claims', 'Inflation Rate', 'Consumer Price Index'
];

const isPositiveEconomicIndicator = (eventName) => {
  if (POSITIVE_INDICATORS.some(indicator => eventName.includes(indicator))) {
    return true;
  }
  if (NEGATIVE_INDICATORS.some(indicator => eventName.includes(indicator))) {
    return false;
  }
  // Default to positive if not explicitly categorized
  return true;
};

const EconomicEventRow = ({
  event,
  expandedRow,
  toggleRow,
  index,
  dateColumnStyle,
  totalColumns, // Receive totalColumns prop
}) => {
  const renderValue = (label, value) => {
    if (value === 'N/A' || value === null || value === undefined) return null;
    const pill = PILL_STYLES[getValueSentiment(label, value)];
    return (
      <span style={pill.pill}>
        <span style={PILL_LABEL_STYLE}>{label}</span>
        <span style={pill.value}>{value}</span>
      </span>
    );
  };

  const getValueSentiment = (label, value) => {
    if (label === 'Actual' && event.forecast && value !== 'N/A' && event.forecast !== 'N/A') {
      const actualValue = parseFloat(value);
      const forecastValue = parseFloat(event.forecast);
      if (!isNaN(actualValue) && !isNaN(forecastValue)) {
        const beat = isPositiveEconomicIndicator(event.name)
          ? actualValue > forecastValue
          : actualValue < forecastValue;
        return beat ? 'positive' : 'negative';
      }
    }
    return 'neutral';
  };

  const renderDetails = (details) => {
    if (!details) return null;
    return (
      <div className="economic-event-details">
        {details.measures && (
          <p><strong>Measures:</strong> {details.measures.replace(/<\/?p>/g, '')}</p>
        )}
        {details.usual_effect && (
          <p><strong>Usual Effect:</strong> {details.usual_effect.replace(/<\/?p>/g, '')}</p>
        )}
        {details.why_care && (
          <p><strong>Why Care:</strong> {details.why_care.replace(/<\/?p>/g, '')}</p>
        )}
        {details.frequency && (
          <p><strong>Frequency:</strong> {details.frequency}</p>
        )}
      </div>
    );
  };

  const isExpanded = expandedRow === `economic-${index}`;

  return (
    <>
      <tr 
        className={`economic-event-row ${isExpanded ? 'expanded' : ''}`}
        onClick={() => toggleRow(`economic-${index}`)}
      >
        <td className="py-2 px-4" style={dateColumnStyle}>
          <div className="text-sm">
            {formatDate(event.date)}
            <br />
            {renderEarningsDays(event.date)}
          </div>
        </td>
        <td className="py-2 px-4" colSpan={totalColumns - 1}> {/* Adjust colSpan */}
          <div className="flex items-center">
            <div className="economic-event-name">{event.name}</div>
            <div className="economic-event-data flex items-center" style={PILL_ROW_STYLE}>
              {renderValue('Actual', event.actual)}
              {renderValue('Forecast', event.forecast)}
              {renderValue('Previous', event.previous)}
            </div>
            <span className="ml-auto economic-event-chevron">
              {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
            </span>
          </div>
          {isExpanded && renderDetails(event.details)}
        </td>
      </tr>
    </>
  );
};

export default EconomicEventRow;
